/**
 * Per-site PWA home-screen icon generator.
 *
 * Draws: site-type pill (top), organization monogram (center), "PTC" anchor
 * (bottom) on a site-type or tenant-colour background. Pure client-side
 * canvas — no network calls. Logos are not drawn (cross-origin images taint
 * the canvas and break export), so the monogram is the identity mark.
 */

export type PwaSiteType =
  | "phc"
  | "trial_site"
  | "cro"
  | "sponsor"
  | "government"
  | "training_cohort"
  | "platform";

export interface PwaIconSpec {
  monogram: string;
  badge: string;
  background: string;
  siteType: PwaSiteType;
}

export const SITE_TYPE_STYLE: Record<PwaSiteType, { badge: string; color: string }> = {
  phc: { badge: "PHC", color: "#16a34a" },
  trial_site: { badge: "CTMS", color: "#0891b2" },
  cro: { badge: "CRO", color: "#2563eb" },
  sponsor: { badge: "SPONSOR", color: "#4f46e5" },
  government: { badge: "GOV", color: "#b45309" },
  training_cohort: { badge: "ACADEMY", color: "#7c3aed" },
  platform: { badge: "PTC", color: "#2563eb" },
};

/** Site type guessed from the TLD alone (boot stage, before data loads). */
export function siteTypeFromTld(tld: string): PwaSiteType {
  switch (tld.toLowerCase()) {
    case "health":
      return "phc";
    case "africa":
      return "government";
    case "academy":
      return "training_cohort";
    default:
      return "trial_site";
  }
}

/** Up to 4 letters: slug as-is when short, else initials of words. */
export function buildMonogram(source: string): string {
  const clean = (source || "").replace(/[^a-zA-Z0-9\s\-_]/g, " ").trim();
  if (!clean) return "PTC";
  const words = clean.split(/[\s\-_]+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 4).toUpperCase();
  return words
    .map((w) => w[0])
    .join("")
    .slice(0, 4)
    .toUpperCase();
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function buildIconSpec(input: {
  slug: string;
  siteType: PwaSiteType;
  primaryColor?: string | null;
}): PwaIconSpec {
  const style = SITE_TYPE_STYLE[input.siteType] ?? SITE_TYPE_STYLE.platform;
  const color = input.primaryColor && HEX.test(input.primaryColor.trim())
    ? input.primaryColor.trim()
    : style.color;
  return {
    monogram: buildMonogram(input.slug),
    badge: style.badge,
    background: color,
    siteType: input.siteType,
  };
}

export const ICON_SIZES = [180, 192, 512] as const;

/** Renders one PNG data URL, or null when canvas is unavailable. */
export function renderIcon(spec: PwaIconSpec, size: number): string | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext?.("2d");
  if (!ctx) return null;

  // Full-bleed background so maskable crops look right on Android.
  ctx.fillStyle = spec.background;
  ctx.fillRect(0, 0, size, size);
  const shade = ctx.createLinearGradient(0, 0, 0, size);
  shade.addColorStop(0, "rgba(255,255,255,0.14)");
  shade.addColorStop(1, "rgba(0,0,0,0.22)");
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, size, size);

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const font = (w: number, px: number) =>
    `${w} ${Math.round(px)}px system-ui, -apple-system, "Segoe UI", sans-serif`;

  // Top pill: site type (kept inside the 80% maskable safe zone).
  const pillFont = size * 0.085;
  ctx.font = font(800, pillFont);
  const pillW = ctx.measureText(spec.badge).width + size * 0.09;
  const pillH = size * 0.13;
  const pillY = size * 0.17;
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  roundRect(ctx, (size - pillW) / 2, pillY, pillW, pillH, pillH / 2);
  ctx.fill();
  ctx.fillStyle = spec.background;
  ctx.fillText(spec.badge, size / 2, pillY + pillH / 2 + size * 0.004);

  // Centre monogram, shrunk to fit.
  let mono = size * (spec.monogram.length <= 2 ? 0.34 : spec.monogram.length === 3 ? 0.27 : 0.22);
  ctx.font = font(900, mono);
  while (ctx.measureText(spec.monogram).width > size * 0.72 && mono > 8) {
    mono -= 2;
    ctx.font = font(900, mono);
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillText(spec.monogram, size / 2, size * 0.52);

  // Bottom PTC anchor.
  ctx.font = font(700, size * 0.07);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fillText("● PTC", size / 2, size * 0.78);

  try {
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function generateDynamicPwaIcons(spec: PwaIconSpec): Record<number, string> | null {
  const out: Record<number, string> = {};
  for (const s of ICON_SIZES) {
    const url = renderIcon(spec, s);
    if (!url) return null;
    out[s] = url;
  }
  return out;
}
